import type { DefLookup } from "./catalog";
import { CONTROL_CIRCUITS } from "./catalog/control-circuits";
import { effectiveTerminals } from "./effective";
import type { System } from "./types";

const controlIds = new Set(CONTROL_CIRCUITS.map(def => def.id));

/**
 * The ordinary current model follows a single contact position. Its result is
 * not a conductor envelope across all positions: even a cable upstream of a
 * selector can carry a larger load on the other throw. Until that envelope is
 * modeled, retain the estimate but mark the entire connected DC circuit as
 * provisional. This deliberately includes shared feeds and returns.
 *
 * Component-level DC connectivity is conservative across DC converters. AC
 * and signal connections cannot join otherwise separate DC circuits here.
 * A retained control ID still needs this guard after an electrical custom edit.
 */
export function controlSizingCableIds(system: System, lookup: DefLookup): Set<string> {
  const controls: string[] = [];
  const posts = new Map<string, Set<string>>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (controlIds.has(node.definitionId) || (def && controlIds.has(def.id))) controls.push(node.id);
    if (def) posts.set(node.id, new Set(effectiveTerminals(def, node)
      .filter(post => !post.id.startsWith("ac-") && post.polarity !== "signal")
      .map(post => post.id)));
  }
  if (!controls.length) return new Set();
  const adjacency = new Map<string, Set<string>>();
  const dcCables = system.cables.filter(cable =>
    posts.get(cable.fromNodeId)?.has(cable.fromTerminalId) &&
    posts.get(cable.toNodeId)?.has(cable.toTerminalId));
  for (const cable of dcCables) {
    if (!adjacency.has(cable.fromNodeId)) adjacency.set(cable.fromNodeId, new Set());
    if (!adjacency.has(cable.toNodeId)) adjacency.set(cable.toNodeId, new Set());
    adjacency.get(cable.fromNodeId)!.add(cable.toNodeId);
    adjacency.get(cable.toNodeId)!.add(cable.fromNodeId);
  }
  const seen = new Set(controls), queue = [...controls];
  for (let index = 0; index < queue.length; index++) {
    for (const other of adjacency.get(queue[index]) ?? []) {
      if (seen.has(other)) continue;
      seen.add(other);
      queue.push(other);
    }
  }
  return new Set(dcCables.filter(cable => seen.has(cable.fromNodeId)).map(cable => cable.id));
}
